import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { KpiCard, SectionTitle } from "@/components/dashboard/widgets";
import { Button } from "@/components/ui/button";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import {
  Copy, Flame, ArrowUpRight, Share2, MousePointer2, UserPlus, Percent, PoundSterling,
  TrendingUp, Sparkles,
} from "lucide-react";
import { copyToClipboard } from "@/lib/clipboard";
import ReferPartnerModal from "@/components/affiliate/ReferPartnerModal";
import TierProgressCard from "@/components/affiliate/TierProgressCard";

function ViralReplays() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/affiliate/viral-replays").then(r => setRows(r.data)).catch(() => setRows([])); }, []);
  if (rows.length === 0) return null;
  return (
    <Card className="rounded-xl shadow-soft border-slate-200" data-testid="viral-replays">
      <CardContent className="p-6">
        <div className="flex items-center gap-2.5 mb-5">
          <Flame className="size-4 text-[#0055FF]" strokeWidth={1.6} />
          <div className="leading-tight">
            <h3 className="font-medium text-slate-900">Viral replays to promote</h3>
            <div className="text-[11.5px] text-slate-500 mt-0.5">Top-watched AI conversations across the platform</div>
          </div>
        </div>
        <ul className="divide-y divide-slate-100">
          {rows.map((r) => (
            <li key={r.id} className="flex items-center justify-between gap-3 py-3 text-sm">
              <div className="min-w-0 flex-1">
                <div className="truncate text-slate-800 font-medium">"{r.intent || "Enquiry replay"}"</div>
                <div className="text-[11px] text-slate-500 capitalize mt-0.5">{(r.sector || "—").toLowerCase().replace("-", " ")}</div>
              </div>
              <div className="inline-flex items-center gap-3 shrink-0">
                <span className="text-[11px] font-mono text-slate-500 tabular-nums">{r.views.toLocaleString()} views</span>
                <a href={`/replay/${r.id}`} target="_blank" rel="noreferrer"
                   className="inline-grid place-items-center size-7 rounded-md bg-slate-50 hover:bg-[#0055FF]/8 text-slate-500 hover:text-[#0055FF] transition-colors">
                  <ArrowUpRight className="size-3.5" />
                </a>
              </div>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

export default function Overview() {
  const [data, setData] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  useEffect(() => { api.get("/affiliate/overview").then(r => setData(r.data)); }, []);
  if (!data) return <div className="text-sm text-slate-500">Loading…</div>;
  const { kpis, series, tracking_link, tier } = data;

  return (
    <div className="space-y-8">
      <SectionTitle
        eyebrow="Affiliate portal"
        title="Welcome back."
        subtitle="Performance over the last 14 days · paid weekly via Stripe Connect Express."
      />

      {/* ===== PREMIUM HERO TRACKING-LINK BLOCK ===== */}
      <div className="relative overflow-hidden rounded-2xl border border-slate-200 shadow-soft bg-white" data-testid="aff-hero">
        <div className="absolute inset-0 dot-grid dot-grid-fade opacity-50 pointer-events-none" />

        <div className="relative p-7 sm:p-8">
          <div className="flex items-start gap-3 flex-wrap">
            <div className="flex-1 min-w-[260px]">
              <div className="inline-flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.18em] text-[#0055FF] font-semibold">
                <Sparkles className="size-3" /> Your tracking link
              </div>
              <div
                className="mt-4 inline-flex items-center gap-2 rounded-lg bg-white border border-slate-200 px-4 py-3 font-mono text-[13.5px] text-slate-900 max-w-full"
                data-testid="aff-link"
              >
                <span className="truncate">{tracking_link}</span>
              </div>
              <div className="mt-2.5 text-[12px] text-slate-500 leading-relaxed max-w-xl">
                90-day first-touch cookie window · share anywhere and every signup that lands on Connexis via this link is yours for life.
              </div>
            </div>
            <div className="flex items-center gap-2 mt-1">
              <Button
                onClick={() => copyToClipboard(tracking_link, "Copied to clipboard")}
                variant="outline"
                className="rounded-full h-10 px-5 border-slate-300"
                data-testid="aff-copy"
              >
                <Copy className="size-4" /> Copy
              </Button>
              <Button
                onClick={() => setShareOpen(true)}
                className="rounded-full h-10 px-5 bg-[#0055FF] hover:bg-[#0044CC] text-white shadow-md shadow-[#0055FF]/20"
                data-testid="aff-refer-cta"
              >
                <Share2 className="size-4" /> Refer a partner
              </Button>
            </div>
          </div>
        </div>
      </div>

      <ReferPartnerModal open={shareOpen} onOpenChange={setShareOpen} trackingLink={tracking_link} />

      {/* ===== TIER PROGRESS — drives recurring activity via progress bias ===== */}
      <TierProgressCard tier={tier} />

      {/* ===== PREMIUM KPI ROW with branded icons ===== */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label="Clicks · 14d"     value={kpis.clicks.toLocaleString()}  icon={MousePointer2} accent="blue"    testid="aff-kpi-clicks" />
        <KpiCard label="Signups · 14d"    value={kpis.signups.toLocaleString()} icon={UserPlus}      accent="blue"    testid="aff-kpi-signups" />
        <KpiCard label="Conversion · 14d" value={`${kpis.conversion}%`}         icon={Percent}       accent="emerald" testid="aff-kpi-conv" />
        <KpiCard label="Commission · 14d" value={kpis.commission.toLocaleString()} prefix="£"        icon={PoundSterling} accent="emerald" testid="aff-kpi-comm" />
      </div>

      {/* ===== Performance chart ===== */}
      <Card className="rounded-xl shadow-soft border-slate-200">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-5">
            <div className="leading-tight">
              <div className="inline-flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.18em] text-[#0055FF] font-semibold">
                <TrendingUp className="size-3" /> Performance
              </div>
              <h3 className="font-medium text-slate-900 mt-1.5">Clicks, signups & commission · last 14 days</h3>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={300} minWidth={0} minHeight={200}>
            <AreaChart data={series} margin={{ left: -8, right: 8, top: 8, bottom: 0 }}>
              <defs>
                <linearGradient id="ac" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0%" stopColor="#0055FF" stopOpacity={0.32} />
                  <stop offset="100%" stopColor="#0055FF" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0", boxShadow: "0 4px 12px rgba(15,23,42,0.06)" }} />
              <Area type="monotone" dataKey="clicks" stroke="#0055FF" fill="url(#ac)" strokeWidth={2.2} />
            </AreaChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <ViralReplays />
    </div>
  );
}
